import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, createRootRouteWithContext, redirect } from '@tanstack/react-router';
import { Button } from '@ui/primitives/button';
import { DangerIcon, ErrorContent, ErrorPageLayout, SadFaceIcon } from '@ui/primitives/error-page';
import { GlobeErrorPage } from '@ui/primitives/globe-error';
import { getAccessToken } from '@ui/token-storage';
// Public routes that don't require authentication
const PUBLIC_ROUTES = [
    '/login',
    '/logout',
    '/auth/callback',
    '/auth/verify-email',
    '/403',
    '/404',
    '/500'
];
/**
 * Root-level error component. Catches errors from beforeLoad, loaders, and any unhandled errors that bubble up past
 * child routes. Renders standalone (no layout shell) since the root layout may itself be broken.
 */ function RootErrorComponent(param) {
    let { error, reset } = param;
    const isDev = process.env.NODE_ENV === 'development';
    return /*#__PURE__*/ _jsx(ErrorPageLayout, {
        children: /*#__PURE__*/ _jsx(ErrorContent, {
            icon: SadFaceIcon,
            title: "Oops!",
            subtitle: "Something went wrong",
            description: "An unexpected error occurred. Please try again or return to the home page.",
            image: /*#__PURE__*/ _jsxs("div", {
                className: "w-full max-w-[300px]",
                children: [
                    /*#__PURE__*/ _jsx(GlobeErrorPage, {}),
                    isDev && error.message && /*#__PURE__*/ _jsx("pre", {
                        className: "mt-4 max-h-32 overflow-auto rounded-md border bg-muted p-3 text-left text-xs text-muted-foreground",
                        children: error.message
                    })
                ]
            }),
            action: /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-3",
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "outline",
                        onClick: reset,
                        children: "Try again"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: "/",
                            children: "Go to Home Page"
                        })
                    })
                ]
            })
        })
    });
}
/** Root-level not found component. Renders standalone (no layout shell). */ function RootNotFoundComponent() {
    return /*#__PURE__*/ _jsx(ErrorPageLayout, {
        children: /*#__PURE__*/ _jsx(ErrorContent, {
            icon: DangerIcon,
            title: "404",
            subtitle: "Page not found",
            description: "The page you're looking for doesn't exist or has been moved.",
            image: /*#__PURE__*/ _jsx("div", {
                className: "w-full max-w-[300px]",
                children: /*#__PURE__*/ _jsx(GlobeErrorPage, {})
            }),
            action: /*#__PURE__*/ _jsx(Button, {
                asChild: true,
                children: /*#__PURE__*/ _jsx(Link, {
                    to: "/",
                    children: "Go to Home Page"
                })
            })
        })
    });
}
export const Route = createRootRouteWithContext()({
    beforeLoad: async (param)=>{
        let { location } = param;
        const isPublicRoute = PUBLIC_ROUTES.some((route)=>location.pathname.startsWith(route));
        // If not a public route, check for token
        if (!isPublicRoute) {
            const token = getAccessToken();
            if (!token) {
                throw redirect({
                    to: '/login',
                    search: {
                        redirect: location.href
                    }
                });
            }
        }
        // If has token but trying to access login, redirect to home
        if (getAccessToken() && location.pathname === '/login') {
            throw redirect({
                to: '/'
            });
        }
    },
    errorComponent: RootErrorComponent,
    notFoundComponent: RootNotFoundComponent
});
